import { useEffect, useMemo, useRef } from 'react';
import type { BulkPreviewItem as InventoryBulkPreviewRow } from '../domain/catalogTypes';
import { bulkCategoryScopeSummary, type BulkCategoryScope } from '../application/bulkCategoryScope';
import type { CategoryOption } from '../domain/categoryOptions';
import { isBulkPreviewScopeAllowed } from '../application/bulkPreviewScope';

export type BulkApplyTo = 'prices' | 'quantities';

export {
  BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT,
  BULK_COEFFICIENT_PREVIEW_PAGE_SIZE,
} from '../application/bulkCoefficientConstants';

/** @deprecated Usar BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT */
export const BULK_COEFFICIENT_PREVIEW_DISPLAY_LIMIT = 500;

export type UseBulkCoefficientModalParams = {
  visible: boolean;
  bulkCategoryScope: BulkCategoryScope;
  bulkSearch: string;
  bulkPercentInput: string;
  bulkApplyTo: BulkApplyTo;
  bulkPreview: InventoryBulkPreviewRow[];
  bulkApplyTotal: number;
  categoryOptions: CategoryOption[];
  onClearPreview?: () => void;
};

export const useBulkCoefficientModal = ({
  visible,
  bulkCategoryScope,
  bulkSearch,
  bulkPercentInput,
  bulkApplyTo,
  bulkPreview,
  bulkApplyTotal,
  categoryOptions,
  onClearPreview,
}: UseBulkCoefficientModalParams) => {
  const scopeSummaryLabel = bulkCategoryScopeSummary(bulkCategoryScope, categoryOptions);

  const previewRows = useMemo(
    () => [...bulkPreview].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [bulkPreview],
  );

  const scopeReady = isBulkPreviewScopeAllowed(bulkCategoryScope, bulkSearch);

  useEffect(() => {
    if (!visible) return;
  }, [visible]);

  const skipClearPreviewOnMountRef = useRef(true);

  useEffect(() => {
    if (skipClearPreviewOnMountRef.current) {
      skipClearPreviewOnMountRef.current = false;
      return;
    }
    if (bulkPreview.length === 0) return;
    onClearPreview?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkCategoryScope, bulkSearch, bulkPercentInput, bulkApplyTo]);

  return {
    scopeSummaryLabel,
    previewRows,
    scopeReady,
    hasPreview: bulkPreview.length > 0,
    previewListedCount: bulkPreview.length,
    applyTotal: bulkApplyTotal,
  };
};
